import { format } from 'date-fns';
import * as Linking from 'expo-linking';
import Linkify from 'react-native-linkify';

import { type PostWithInquiryLibraryAnswers, readInquiryLibraryAnswers } from '../../model/answers';
import type { SharedLibraryPost } from '../../model/types';
import { InquiryLibraryAnswersBody } from '../answers/InquiryLibraryAnswersBody';
import { postDateClassName } from '../answers/answersLayout';

import { Heading } from '@/components/heading';
import { Badge, BadgeText } from '@/components/ui/badge';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

/**
 * The share response with the submitter's question, which the signed-in post route already sends.
 *
 * TODO: drop this once the types are regenerated. The primum-infra change on
 * `feat/public-post-question` adds `question` (nullable, the same shape as on
 * `GET /v2/community-posts/{id}`) to `GET /v2/public/community-posts/{token}`. Until it deploys
 * and `pnpm run typegen:dev` picks it up, the field is read as optional, so a response without it
 * keeps the flat body.
 */
export type SharedPost = SharedLibraryPost & Pick<PostWithInquiryLibraryAnswers, 'question'>;

const formatAuthorName = (author: SharedPost['author']) => {
  if (!author) {
    return '';
  }

  const fullName = [author.firstName, author.lastName].filter(Boolean).join(' ').trim();

  if (!fullName) {
    return '';
  }

  return author.identifiesAsDoctor ? `Dr. ${fullName}` : fullName;
};

/**
 * A shared post as a signed-out reader sees it at `/community/shared/{token}`: cancer type and
 * date, the title, the author, then the body.
 *
 * With INQUIRY_LIBRARY_ANSWERS on, and the response carrying a question and at least one answer with
 * replies, the body is replaced by the same answers the signed-in post shows: the question card,
 * then the numbered Primum Specialist answers with their replies, follow-ups and times. Otherwise,
 * including every response from an API that does not send `question` yet, it keeps the flat body.
 * `readInquiryLibraryAnswers` makes that call, the same as on the signed-in post, so the two never disagree.
 */
export function SharedPostBody({ post }: { post: SharedPost }) {
  const isInquiryLibraryAnswersEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_LIBRARY_ANSWERS);

  /** Null whenever the flag is off or the post has no answers data, which keeps the body. */
  const inquiryLibraryAnswers = isInquiryLibraryAnswersEnabled
    ? readInquiryLibraryAnswers(post)
    : null;

  const publishedAtDate = post.publishedAt ? format(new Date(post.publishedAt), 'MM/dd/yyyy') : '';

  const authorName = formatAuthorName(post.author);

  const authorSubtitle = [post.author?.specialty, post.author?.institution]
    .filter(Boolean)
    .join(' · ');

  return (
    <VStack className="gap-4 px-5 pb-8 pt-6">
      <HStack className="items-center justify-between pt-4 md:pt-0">
        <Badge action="muted" size="md">
          <BadgeText>{post.cancerType?.shortLabel || post.cancerType?.label || ''}</BadgeText>
        </Badge>
        <Text size="sm" className={postDateClassName(inquiryLibraryAnswers !== null)}>
          {publishedAtDate}
        </Text>
      </HStack>

      <Heading size="h2-md" className="font-semibold">
        {post.title}
      </Heading>

      {authorName ? (
        <VStack>
          <Text size="sm" className="font-semibold text-typography-700">
            {authorName}
          </Text>
          {authorSubtitle ? (
            <Text size="sm" className="text-typography-500">
              {authorSubtitle}
            </Text>
          ) : null}
        </VStack>
      ) : null}

      {inquiryLibraryAnswers ? (
        <InquiryLibraryAnswersBody post={inquiryLibraryAnswers} postId={post.id} />
      ) : (
        <Linkify
          linkStyle={{ textDecorationLine: 'underline' }}
          onPress={(url) => Linking.openURL(url)}>
          <Text className="font-body text-base leading-relaxed">{post.body}</Text>
        </Linkify>
      )}
    </VStack>
  );
}
